// src/components/ClientModal.js
import React, { useState, useEffect, useRef } from 'react';
import PlatformBlock from './PlatformBlock';
import ReportModal from './ReportModal';
import ImportSurveyModal from './ImportSurveyModal';
import SurveyLibraryModal from './SurveyLibraryModal';
import { useAuth } from '../AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  PHASES, PLATFORMS, ADS_DESTINATIONS, LP_STEPS, TRACK_STEPS, PM_STEPS,
  VERTICALS, newPlatform, platShort,
} from '../constants';

const FG = ({ children, full, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: full ? '1/-1' : undefined, ...style }}>
    {children}
  </div>
);

const FL = ({ children }) => (
  <label style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children}
  </label>
);

const Input = (props) => (
  <input {...props} style={{
    width: '100%', fontSize: 13, padding: '6px 9px',
    border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
    background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
    boxSizing: 'border-box', ...props.style,
  }} />
);

const Sel = ({ children, ...props }) => (
  <select {...props} style={{
    width: '100%', fontSize: 13, padding: '6px 9px',
    border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
    background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
    ...props.style,
  }}>
    {children}
  </select>
);

const TA = (props) => (
  <textarea {...props} style={{
    width: '100%', fontSize: 13, padding: '6px 9px',
    border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
    background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
    resize: 'vertical', minHeight: 80, boxSizing: 'border-box', ...props.style,
  }} />
);

const SectionDivider = ({ label, color = '#185FA5', icon = '' }) => (
  <div style={{
    gridColumn: '1/-1',
    marginTop: 8, marginBottom: 2,
    borderLeft: `4px solid ${color}`,
    paddingLeft: 10,
    display: 'flex', alignItems: 'center', gap: 6,
  }}>
    {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
    <span style={{ fontSize: 12, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    <div style={{ flex: 1, height: 1, background: `${color}22`, marginLeft: 6 }} />
  </div>
);

function LinkField({ label, value, onChange }) {
  return (
    <FG>
      <FL>{label}</FL>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Input
          type="url"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="https://..."
          style={{ flex: 1 }}
        />
        {value && (
          <a href={value} target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: '#185FA5', whiteSpace: 'nowrap', textDecoration: 'none' }}>
            Open ↗
          </a>
        )}
      </div>
    </FG>
  );
}

function CollapsibleSection({ title, icon, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 0' }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>{icon} {title}</span>
        {value && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 4 }}>•</span>}
        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && (
        <TA
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ minHeight: 70, fontSize: 12 }}
        />
      )}
    </div>
  );
}

function CollapsibleBlock({ title, icon, defaultOpen = false, extra, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ gridColumn: '1/-1', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 9, marginTop: 6 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          padding: '9px 12px', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 800, color: '#1a1a18' }}>{icon} {title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {extra}
          <span style={{ fontSize: 10, color: '#aaa' }}>{open ? '▼' : '▶'}</span>
        </span>
      </div>
      {open && (
        <div style={{
          padding: '4px 12px 14px', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px 14px',
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function DateDiffCallout({ label, fromValue, toValue }) {
  const days = (fromValue && toValue)
    ? Math.round((new Date(toValue) - new Date(fromValue)) / 86400000)
    : null;
  return (
    <FG>
      <FL>{label}</FL>
      <div style={{
        background: days !== null ? '#EAF3DE' : '#f5f5f3',
        borderRadius: 7, border: '1px solid rgba(0,0,0,0.18)',
        padding: '6px 9px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: days !== null ? '#27500A' : '#aaa' }}>
          {days !== null ? days : '—'}
        </div>
        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>days</div>
      </div>
    </FG>
  );
}


function StepChecklist({ steps, values, onChange, accentColor = '#185FA5', disabled = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {steps.map((s, i) => {
        const done = !!values?.[s];
        return (
          <div key={s} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 8px', borderRadius: 7, fontSize: 12,
            background: done ? '#f0faf4' : 'transparent',
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
              background: done ? '#EAF3DE' : '#f0f0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
              color: done ? '#27500A' : '#999',
              border: done ? 'none' : '1px solid rgba(0,0,0,0.12)',
            }}>
              {done ? '✓' : i + 1}
            </div>
            <input
              type="checkbox"
              checked={done}
              onChange={e => !disabled && onChange({ ...values, [s]: e.target.checked })}
              disabled={disabled}
              style={{ accentColor, width: 14, height: 14, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: disabled ? 0.5 : 1 }}
            />
            <span style={{ color: done ? '#999' : 'inherit', textDecoration: done ? 'line-through' : 'none' }}>
              {s}
            </span>
          </div>
        );
      })}
    </div>
  );
}



function VerticalSelector({ selected, onChange }) {
  const [customInput, setCustomInput] = React.useState('');
  const hasGarageDoors = selected.includes('Garage Doors');

  // Custom verticals = selected items not in the standard list
  const customSelected = selected.filter(v => !VERTICALS.includes(v));

  function addCustom() {
    const val = customInput.trim();
    if (!val || selected.includes(val)) { setCustomInput(''); return; }
    onChange([...selected, val]);
    setCustomInput('');
  }

  function toggle(v) {
    if (selected.includes(v)) onChange(selected.filter(s => s !== v));
    else onChange([...selected, v]);
  }

  return (
    <div>
      {/* Standard verticals */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0' }}>
        {VERTICALS.map(v => {
          const active = selected.includes(v);
          const isSpecial = v === 'Garage Doors';
          return (
            <button
              key={v}
              type="button"
              onClick={() => toggle(v)}
              style={{
                fontSize: 12, padding: '4px 11px', borderRadius: 20,
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: active ? 700 : 400,
                border: active ? `2px solid ${isSpecial ? '#BA7517' : '#185FA5'}` : '2px solid rgba(0,0,0,0.15)',
                background: active ? (isSpecial ? '#FAEEDA' : '#E6F1FB') : '#f5f5f3',
                color: active ? (isSpecial ? '#854F0B' : '#185FA5') : '#555',
                transition: 'all 0.12s',
              }}
            >
              {isSpecial && active ? '⚠ ' : ''}{v}
            </button>
          );
        })}

        {/* Custom verticals already added */}
        {customSelected.map(v => (
          <button
            key={v}
            type="button"
            onClick={() => toggle(v)}
            style={{
              fontSize: 12, padding: '4px 11px', borderRadius: 20,
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              border: '2px solid #1D9E75',
              background: '#E1F5EE', color: '#0F6E56',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {v}
            <span style={{ fontSize: 11, opacity: 0.7 }}>✕</span>
          </button>
        ))}
      </div>

      {/* Custom vertical input */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
        <input
          type="text"
          value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add custom vertical…"
          style={{
            fontSize: 12, padding: '4px 10px', borderRadius: 20,
            border: '2px dashed rgba(0,0,0,0.2)', background: '#f5f5f3',
            color: '#555', fontFamily: 'inherit', width: 180, outline: 'none',
          }}
        />
        {customInput.trim() && (
          <button
            type="button"
            onClick={addCustom}
            style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 20,
              background: '#1D9E75', color: '#fff', border: 'none',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
            }}
          >
            + Add
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
          Selected: {selected.join(', ')}
        </div>
      )}
      {hasGarageDoors && (
        <div style={{
          marginTop: 8, padding: '8px 12px', borderRadius: 7,
          background: '#FAEEDA', border: '1px solid #f0d090',
          fontSize: 12, color: '#854F0B', fontWeight: 600,
        }}>
          ⚠ Garage Doors selected — Advanced Verification deliverables will be added to Google Ads &amp; LSA automatically
        </div>
      )}
    </div>
  );
}


function CollapsibleProcess({ title, done, total, accentColor, trackColor, doneColor, defaultOpen = false, disabled = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const allDone = done === total && total > 0;

  return (
    <div style={{ gridColumn: '1/-1', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 10, marginTop: 2 }}>
      {/* Header row — always visible, click to toggle */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: open ? 10 : 6 }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: allDone ? doneColor : accentColor }}>
          {title}
        </span>
        {disabled && <span style={{ fontSize: 10, color: '#aaa', fontStyle: 'italic' }}>view only</span>}
        <span style={{ fontSize: 11, color: allDone ? doneColor : accentColor, whiteSpace: 'nowrap' }}>
          {allDone ? '✓ Complete' : `${done}/${total}`}
        </span>
        {/* Progress bar — always visible */}
        <div style={{ flex: 1, height: 7, background: trackColor, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: allDone ? doneColor : accentColor,
            width: `${pct}%`,
            transition: 'width 0.3s',
          }} />
        </div>
        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 2 }}>{open ? '▼' : '▶'}</span>
      </div>
      {/* Collapsible content */}
      {open && (
        <div style={{ paddingBottom: 6 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function AdsUrlsSection({ websiteUrls, lpUrls, onWebsite, onLp }) {
  const [open, setOpen] = useState(false);
  const hasData = !!(websiteUrls || lpUrls);
  return (
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 0' }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>🌐 Ads URLs</span>
        {hasData && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 4 }}>•</span>}
        <span style={{ fontSize: 11, color: '#bbb' }}>Website & Landing Page URLs used on ads</span>
        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 10 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
              Website URL(s) on Ads
            </label>
            <textarea
              value={websiteUrls || ''}
              onChange={e => onWebsite(e.target.value)}
              placeholder="e.g. https://example.com/hvac"
              style={{
                width: '100%', fontSize: 12, padding: '6px 9px',
                border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
                background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
                resize: 'vertical', minHeight: 60, boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
              Landing Page URL(s) on Ads
            </label>
            <textarea
              value={lpUrls || ''}
              onChange={e => onLp(e.target.value)}
              placeholder="e.g. https://lp.example.com/hvac-repair"
              style={{
                width: '100%', fontSize: 12, padding: '6px 9px',
                border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
                background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
                resize: 'vertical', minHeight: 60, boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function buildOnboardingAnalysis(client) {
  const platforms = client.platforms || [];
  const getCid = name => {
    const p = platforms.find(x => x.name === name);
    if (!p) return '';
    if (p.accountType === 'new') return 'Build New';
    return p.cid || '';
  };
  const coupons = (client.couponsPromos || '').split('\n').map(l => l.trim()).filter(Boolean);
  const L = [];
  L.push('Paid Media Onboarding Analysis:');
  L.push(''); L.push('');
  L.push('Access Requested to Accounts - List Account CIDs or "Build New" below');
  L.push('');
  L.push(`* Google Ads: ${getCid('Google Ads')}`);
  L.push(`* LSA: ${getCid('LSA')}`);
  L.push(`* Microsoft: ${getCid('Microsoft')}`);
  L.push(`* Meta: ${getCid('Meta')}`);
  L.push('   * Can we request access to these accounts yet? If not, explain when:');
  L.push(''); L.push('');
  L.push('Call Tracking');
  L.push('');
  L.push('* Standard');
  L.push(`* Forward To # ${client.phoneNumbers || ''}`);
  L.push(''); L.push('');
  L.push('* Custom');
  L.push('* Forward To # - Campaign/Channel');
  L.push('* Forward To # - Campaign/Channel');
  L.push('* Forward To # - Campaign/Channel');
  L.push(''); L.push('');
  L.push('* F/R # (LPs Only)');
  L.push(''); L.push('');
  L.push('Ad Schedules');
  L.push('');
  L.push(`* Google Ads: ${client.adScheduleNotes || ''}`);
  L.push('* LSA:');
  L.push('* Microsoft:');
  L.push(''); L.push('');
  L.push('Location Targeting');
  L.push(`      City/State: ${client.serviceAreaNotes || ''}`);
  L.push('');
  L.push('* Google Ads:');
  L.push('* LSA:');
  L.push('* Microsoft:');
  L.push(''); L.push('');
  L.push('Coupons:');
  L.push('');
  L.push(`* Coupon 1: ${coupons[0] || ''}`);
  L.push(`* Coupon 2: ${coupons[1] || ''}`);
  L.push(`* Coupon 3: ${coupons[2] || ''}`);
  L.push(''); L.push('');
  L.push(`Contact Form Email: (If LP's) ${client.formContactEmail || ''}`);
  L.push(''); L.push('');
  L.push('Paid Media Strategy: (What Channels/Campaigns/Ads will we be using)');
  L.push('');
  L.push(`* Verticals Targeting: ${(client.verticals || []).join(', ')}`);
  L.push(''); L.push('');
  L.push('Channels Confirmed:');
  L.push('');
  ['Google Ads', 'LSA', 'Microsoft', 'Meta', 'Yelp'].forEach(name => {
    const has = platforms.some(p => p.name === name);
    L.push(`* ${name}${has ? ' ✓' : ''}`);
  });
  L.push(''); L.push('');
  L.push('* Priority Services Targeting:');
  L.push(''); L.push('');
  L.push('* Campaign Structure for Search:');
  L.push(''); L.push('');
  L.push('* RYNO - CG-Branded');
  L.push('* Branded');
  L.push(''); L.push('');
  L.push('* RYNO - CG-Campaign');
  L.push('* Ad Group');
  if (client.campaignStructure) { L.push(''); L.push(client.campaignStructure); }
  L.push(''); L.push('');
  L.push('Value Props:');
  L.push('');
  L.push('Tagline:');
  L.push('Emergency Services:');
  L.push('Financing Available:');
  L.push('Year Founded:');
  if (client.valueProps) { L.push(''); L.push(client.valueProps); }
  L.push(''); L.push('');
  L.push('NOTES:');
  L.push('');
  return L.join('\n');
}

function OnboardingAnalysisSection({ form, set }) {
  const [open, setOpen] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');
  const taRef = useRef(null);

  function handleToggle() {
    if (!open && !form.onboardingAnalysisContent) {
      set('onboardingAnalysisContent', buildOnboardingAnalysis(form));
    }
    setOpen(!open);
  }

  function handleRegenerate() {
    if (window.confirm("Regenerate from current client data? This will overwrite anything typed in this box.")) {
      set('onboardingAnalysisContent', buildOnboardingAnalysis(form));
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(form.onboardingAnalysisContent || '');
      setCopyMsg('Copied ✓');
    } catch {
      taRef.current?.select();
      document.execCommand('copy');
      setCopyMsg('Copied ✓');
    }
    setTimeout(() => setCopyMsg(''), 1800);
  }

  return (
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 8 }}>
      <div
        onClick={handleToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 0' }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>📋 Paid Media Onboarding Analysis</span>
        {form.onboardingAnalysisContent && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 4 }}>•</span>}
        <span style={{ fontSize: 11, color: '#bbb' }}>Auto-filled, editable, copy-pasteable</span>
        <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && (
        <div style={{ paddingBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <button type="button" onClick={handleCopy} style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 6,
              background: '#EAF3DE', color: '#27500A', border: '1px solid #b8dfa0',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
            }}>
              ⧉ Copy
            </button>
            <button type="button" onClick={handleRegenerate} style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 6,
              background: '#f5f5f3', color: '#555', border: '1px solid rgba(0,0,0,0.15)',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
            }}>
              🔄 Regenerate from client data
            </button>
            {copyMsg && <span style={{ fontSize: 11, color: '#0F6E56', fontWeight: 700 }}>{copyMsg}</span>}
          </div>
          <textarea
            ref={taRef}
            value={form.onboardingAnalysisContent || ''}
            onChange={e => set('onboardingAnalysisContent', e.target.value)}
            style={{
              width: '100%', minHeight: 420, fontSize: 12, lineHeight: 1.5,
              padding: 10, border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7,
              background: '#fafaf8', color: '#1a1a18', fontFamily: 'inherit',
              resize: 'vertical', boxSizing: 'border-box',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function ClientModal({ client, onSave, onDelete, onClose, onDuplicate }) {
  const { userProfile } = useAuth();
  const isViewer = userProfile?.role === 'viewer';
  const isPM = userProfile?.role === 'pm' || userProfile?.role === 'admin'; // supervisor excluded intentionally
  const [teamUsers, setTeamUsers] = useState([]);

  useEffect(() => {
    getDocs(collection(db, 'users')).then(snap => {
      setTeamUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.role !== 'viewer' && u.role !== 'pm'));
    });
  }, []);
  const [form, setForm] = useState({ ...client });
  const [saving, setSaving] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showImportSurvey, setShowImportSurvey] = useState(false);
  const [showSurveyLibrary, setShowSurveyLibrary] = useState(false);

  useEffect(() => {
    setForm({ ...client });
  }, [client]);

  // Real-time autosave: a couple seconds after any change, silently persist so an
  // accidental close (misclick, stray Backspace, etc.) never loses in-progress edits.
  const [autosavedAt, setAutosavedAt] = useState(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!form.name?.trim()) return; // don't autosave a client with no name yet
    const t = setTimeout(async () => {
      try {
        await onSave({ ...form, updatedAt: Date.now() });
        setAutosavedAt(Date.now());
      } catch (e) {
        console.error('Autosave failed:', e);
      }
    }, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form]);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function handlePlatformChange(index, updated) {
    const platforms = [...form.platforms];
    platforms[index] = updated;
    // Auto-calculate MRR only if at least one platform has both budget AND feePercent
    const hasCalcData = platforms.some(p => parseFloat(p.budget) > 0 && parseFloat(p.feePercent) > 0);
    if (hasCalcData) {
      const autoMRR = platforms.reduce((sum, p) => {
        const b = parseFloat(p.budget) || 0;
        const f = parseFloat(p.feePercent) || 0;
        return (b > 0 && f > 0) ? sum + Math.round(b * f / 100) : sum;
      }, 0);
      setForm(prev => ({ ...prev, platforms, mrrAmount: autoMRR }));
    } else {
      setForm(prev => ({ ...prev, platforms }));
    }
  }

  function removePlatform(index) {
    set('platforms', form.platforms.filter((_, i) => i !== index));
  }

  function addPlatform(name) {
    set('platforms', [...form.platforms, newPlatform(name)]);
  }

  const totalBudget = (form.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);

  const daysToLaunch = (form.kickoff && form.actualLaunch)
    ? Math.round((new Date(form.actualLaunch) - new Date(form.kickoff)) / 86400000)
    : null;

  async function handleSave() {
    if (!form.name?.trim()) { alert('Client name is required.'); return; }
    setSaving(true);
    await onSave({ ...form, updatedAt: Date.now() });
    setSaving(false);
    onClose();
  }

  const isNew = !client.createdAt || client.name === '';
  const adsDestIsLP = form.adsDestination === 'Landing Pages';

  const lpStepsDone = LP_STEPS.filter(s => form.lpSteps?.[s]).length;
  const trackStepsDone = TRACK_STEPS.filter(s => form.trackSteps?.[s]).length;
  const pmStepsDone = PM_STEPS.filter(s => form.pmSteps?.[s]).length;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 200, padding: 20, overflowY: 'auto',
  };

  const modalStyle = {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 860,
    border: '1px solid rgba(0,0,0,0.15)', margin: 'auto',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{
          padding: '13px 18px', borderBottom: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          position: 'sticky', top: 0, background: '#fff',
          borderRadius: '12px 12px 0 0', zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, flex: 1, color: '#1a1a18' }}>
            {isNew ? '+ New Client' : form.name || 'Edit Client'}
          </div>
          {totalBudget > 0 && (
            <div style={{ fontSize: 13, fontWeight: 700, color: '#185FA5' }}>
              Total: ${totalBudget.toLocaleString()}/mo
            </div>
          )}
          <button onClick={() => setShowImportSurvey(true)} style={{
            fontSize: 12, padding: '6px 12px', borderRadius: 7,
            background: '#FAEEDA', color: '#854F0B', border: '1px solid #f0d090',
            cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>
            📥 Import Survey
          </button>
          {!isNew && Object.keys(form.surveys || {}).length > 0 && (
            <button onClick={() => setShowSurveyLibrary(true)} style={{
              fontSize: 12, padding: '6px 12px', borderRadius: 7,
              background: '#f5f5f3', color: '#555', border: '1px solid rgba(0,0,0,0.15)',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
              📚 Surveys ({Object.keys(form.surveys || {}).length})
            </button>
          )}
          {!isNew && (
            <button onClick={() => setShowReport(true)} style={{
              fontSize: 12, padding: '6px 12px', borderRadius: 7,
              background: '#EEEDFE', color: '#3C3489', border: '1px solid #d4d0f7',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
              📋 Run Report
            </button>
          )}
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: '#999', padding: '2px 6px',
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{
          padding: '14px 18px',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
          position: 'relative',
          background: '#fafaf8',
        }}>
          {isViewer && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              cursor: 'not-allowed', borderRadius: 8,
            }} title="View-only access" />
          )}

          {/* Client Name + Phase side by side at top */}
          <FG style={{ gridColumn: '1/-1', display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <FL>Client Name *</FL>
              <Input
                value={form.name || ''}
                onChange={e => set('name', e.target.value)}
                placeholder="Client name"
                style={{ fontSize: 17, fontWeight: 800 }}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <FL>Phase</FL>
              <Sel value={form.phase || 'access'} onChange={e => set('phase', e.target.value)}
                style={{ fontWeight: 700, fontSize: 13 }}>
                {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </Sel>
            </div>
          </FG>

          {form.legalBusinessName ? (
            <FG full style={{ gridColumn: '1/-1' }}>
              <FL>Legal Business Name (from Website Survey)</FL>
              <Input
                value={form.legalBusinessName || ''}
                onChange={e => set('legalBusinessName', e.target.value)}
                placeholder="Official/legal entity name, if different from Client Name"
              />
            </FG>
          ) : null}

          {/* Platforms */}
          <FG full>
            <FL>Platforms</FL>
            {(form.platforms || []).map((p, i) => (
              <PlatformBlock
                key={i}
                plat={p}
                index={i}
                onChange={handlePlatformChange}
                onRemove={removePlatform}
                verticals={Array.isArray(form.verticals) ? form.verticals : (form.vertical ? [form.vertical] : [])}
              />
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
              <Sel
                value=""
                onChange={e => { if (e.target.value) addPlatform(e.target.value); }}
                style={{ width: 'auto', fontSize: 11, padding: '4px 8px' }}
              >
                <option value="">+ Add Platform…</option>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </Sel>
            </div>
          </FG>

          {/* CID / ID Numbers */}
          <FG full>
            <FL>CID / ID Numbers (shown on card)</FL>
            <Input
              value={form.cids || ''}
              onChange={e => set('cids', e.target.value)}
              placeholder="Primary CID or ID number(s)"
            />
          </FG>

          {/* Vertical */}
          <FG full>
            <FL>Vertical(s) — select all that apply</FL>
            <VerticalSelector
              selected={Array.isArray(form.verticals) ? form.verticals : (form.vertical ? [form.vertical] : [])}
              onChange={v => set('verticals', v)}
            />
          </FG>

          {/* Ads Destination */}
          <FG>
            <FL>Ads Destination</FL>
            <Sel
              value={form.adsDestination || ''}
              onChange={e => set('adsDestination', e.target.value)}
            >
              <option value="">— Select —</option>
              {ADS_DESTINATIONS.map(o => <option key={o}>{o}</option>)}
            </Sel>
          </FG>

          {/* Assign to tracker owner — PM/admin only */}
          {isPM && teamUsers.length > 0 && (
            <FG full>
              <FL style={{ color: '#27500A' }}>📋 Assign to Tracker (Owner)</FL>
              <Sel
                value={form.ownerUid || ''}
                onChange={e => {
                  const u = teamUsers.find(u => u.uid === e.target.value);
                  set('ownerUid', e.target.value);
                  set('ownerName', u ? (u.displayName || u.email) : '');
                }}
              >
                <option value="">— Select team member —</option>
                {teamUsers.map(u => (
                  <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>
                ))}
              </Sel>
              {form.ownerUid && (
                <div style={{ fontSize: 11, color: '#27500A', marginTop: 2 }}>
                  ✓ This client will appear in {teamUsers.find(u => u.uid === form.ownerUid)?.displayName || 'their'}'s tracker
                </div>
              )}
            </FG>
          )}

          {/* CSM */}
          <FG>
            <FL>CSM</FL>
            <Input
              value={form.csm || ''}
              onChange={e => set('csm', e.target.value)}
              placeholder="Name"
            />
          </FG>

          {/* PM */}
          <FG>
            <FL>Project Manager</FL>
            <Input
              value={form.pm || ''}
              onChange={e => set('pm', e.target.value)}
              placeholder="Name"
            />
          </FG>

          {/* Strategist */}
          <FG>
            <FL>Paid Media Strategist</FL>
            <Input
              value={form.strategist || ''}
              onChange={e => set('strategist', e.target.value)}
              placeholder="Name"
            />
          </FG>

          {/* Ops Specialist */}
          <FG>
            <FL>Ops Specialist</FL>
            <Input
              value={form.opsSpecialist || ''}
              onChange={e => set('opsSpecialist', e.target.value)}
              placeholder="Name"
            />
          </FG>

          <SectionDivider label="Account Info" color="#185FA5" icon="💼" />

          {/* Paid Media MRR — auto-calculated or manual override */}
          <FG>
            <FL style={{ color: '#185FA5' }}>💰 Paid Media MRR</FL>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, color: '#888' }}>$</span>
              <Input
                type="number"
                value={form.mrrAmount || ''}
                onChange={e => set('mrrAmount', parseFloat(e.target.value) || '')}
                placeholder="Auto-calculated from platforms"
                style={{ fontSize: 16, fontWeight: 700, color: '#185FA5' }}
              />
              <span style={{ fontSize: 12, color: '#888' }}>/mo</span>
            </div>
            {(form.platforms || []).some(p => p.budget && p.feePercent) && (
              <div style={{ fontSize: 11, color: '#0F6E56', marginTop: 3 }}>
                ✓ Auto-calculated from platform budgets × fee %
                {(form.platforms || []).filter(p => p.budget && p.feePercent).map((p, i) => (
                  <span key={i} style={{ marginLeft: 6, color: '#888' }}>
                    {p.name}: ${Math.round((parseFloat(p.budget)||0)*(parseFloat(p.feePercent)||0)/100).toLocaleString()}
                  </span>
                ))}
              </div>
            )}
          </FG>

          {/* Account POC & POC Email */}
          <FG>
            <FL>Account POC</FL>
            <Input
              value={form.accountPoc || ''}
              onChange={e => set('accountPoc', e.target.value)}
              placeholder="Point of contact name"
            />
          </FG>
          <FG>
            <FL>POC Email</FL>
            <Input
              type="email"
              value={form.pocEmail || ''}
              onChange={e => set('pocEmail', e.target.value)}
              placeholder="poc@client.com"
            />
          </FG>

          <SectionDivider label="Links" color="#854F0B" icon="🔗" />

          <LinkField label="Wrike Project Link" value={form.wrinkleLink} onChange={v => set('wrinkleLink', v)} />
          <LinkField label="Analysis Link" value={form.analysisLink} onChange={v => set('analysisLink', v)} />
          <LinkField label="Ad Copy Document" value={form.adCopyDocLink} onChange={v => set('adCopyDocLink', v)} />
          <LinkField label="Meta Design Assets Link" value={form.metaDesignAssetsLink} onChange={v => set('metaDesignAssetsLink', v)} />
          <LinkField label="Salesforce Link" value={form.salesforceLink} onChange={v => set('salesforceLink', v)} />
          <LinkField label="LP Build Template Link" value={form.lpBuildTemplateLink} onChange={v => set('lpBuildTemplateLink', v)} />
          <LinkField label="Main Website URL" value={form.websiteUrl} onChange={v => set('websiteUrl', v)} />
          <LinkField label="GBP Link" value={form.gbpLink} onChange={v => set('gbpLink', v)} />

          <SectionDivider label="Notes" color="#5F5E5A" icon="📝" />

          <FG full>
            <FL>Notes</FL>
            <TA
              value={form.notes || ''}
              onChange={e => set('notes', e.target.value)}
              placeholder="Account status, hold-ups, important details..."
              style={{ minHeight: 100 }}
            />
          </FG>

          <FG full>
            <FL style={{ color: '#854F0B' }}>📌 Pinned Note (shows on card)</FL>
            <Input
              value={form.pinNote || ''}
              onChange={e => set('pinNote', e.target.value)}
              placeholder="e.g. Waiting on owner approval..."
            />
          </FG>

          <CollapsibleSection
            title="Survey / Onboarding Doc Notes" icon="🗒️"
            value={form.surveyNotes} onChange={v => set('surveyNotes', v)}
            placeholder="Notes pulled in from imported surveys and the onboarding doc land here — kept separate from Notes and Pinned Note, which are never auto-filled"
          />

          <CollapsibleSection
            title="Special Notes (For Launch Email)" icon="📧"
            value={form.specialNotesLaunch} onChange={v => set('specialNotesLaunch', v)}
            placeholder="One item per line — each line becomes its own bullet under Special Notes in the Run Report launch email"
          />

          <SectionDivider label="Dates" color="#0F6E56" icon="📅" />

          {/* Dates */}
          <FG>
            <FL>Kick-Off Date</FL>
            <Input type="date" value={form.kickoff || ''} onChange={e => set('kickoff', e.target.value)} />
          </FG>
          <FG>
            <FL>Projected Launch</FL>
            <Input type="date" value={form.projLaunch || ''} onChange={e => set('projLaunch', e.target.value)} />
          </FG>
          <FG>
            <FL>Actual Launch</FL>
            <Input type="date" value={form.actualLaunch || ''} onChange={e => set('actualLaunch', e.target.value)} />
          </FG>
          <FG>
            <FL>Days to Launch</FL>
            <div style={{
              background: '#f5f5f3', borderRadius: 7, border: '1px solid rgba(0,0,0,0.18)',
              padding: '6px 9px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#185FA5' }}>
                {daysToLaunch !== null ? daysToLaunch : '—'}
              </div>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>days</div>
            </div>
          </FG>

          <CollapsibleBlock title="Milestone Dates" icon="📆">
            <FG>
              <FL>Post Sale Call</FL>
              <Input type="date" value={form.postSaleCallDate || ''} onChange={e => set('postSaleCallDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Kick-Off Date <span style={{ fontWeight: 400, textTransform: 'none' }}>(set above)</span></FL>
              <Input type="date" value={form.kickoff || ''} disabled style={{ background: '#eee', color: '#888' }} />
            </FG>
            <FG>
              <FL>Surveys Completed</FL>
              <Input type="date" value={form.surveysCompletedDate || ''} onChange={e => set('surveysCompletedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>All Deliverables Received</FL>
              <Input type="date" value={form.allDeliverablesReceivedDate || ''} onChange={e => set('allDeliverablesReceivedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Landing Pages Ordered</FL>
              <Input type="date" value={form.lpOrderedDate || ''} onChange={e => set('lpOrderedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Landing Pages Completed</FL>
              <Input type="date" value={form.lpCompletedDate || ''} onChange={e => set('lpCompletedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Landing Pages Approved</FL>
              <Input type="date" value={form.lpApprovedDate || ''} onChange={e => set('lpApprovedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Build Posted</FL>
              <Input type="date" value={form.buildPostedDate || ''} onChange={e => set('buildPostedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Tracking Completed <span style={{ fontWeight: 400, textTransform: 'none' }}>(Gladys' phone lines)</span></FL>
              <Input type="date" value={form.trackingCompletedDate || ''} onChange={e => set('trackingCompletedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Paid Tracking Completed</FL>
              <Input type="date" value={form.paidTrackingCompletedDate || ''} onChange={e => set('paidTrackingCompletedDate', e.target.value)} />
            </FG>
            <FG>
              <FL>Completed By</FL>
              <Input value={form.paidTrackingCompletedBy || ''} onChange={e => set('paidTrackingCompletedBy', e.target.value)} placeholder="Name" />
            </FG>
            <FG>
              <FL>Launch Date <span style={{ fontWeight: 400, textTransform: 'none' }}>(set above)</span></FL>
              <Input type="date" value={form.actualLaunch || ''} disabled style={{ background: '#eee', color: '#888' }} />
            </FG>

            <DateDiffCallout label="Kick-Off → Launch" fromValue={form.kickoff} toValue={form.actualLaunch} />
            <DateDiffCallout label="All Deliverables → Launch" fromValue={form.allDeliverablesReceivedDate} toValue={form.actualLaunch} />
          </CollapsibleBlock>

          <CollapsibleBlock
            title="LSA Dates"
            icon="📍"
            defaultOpen={!!form.lsaDatesEnabled}
            extra={
              <label
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: form.lsaDatesEnabled ? '#185FA5' : '#aaa', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={!!form.lsaDatesEnabled}
                  onChange={e => set('lsaDatesEnabled', e.target.checked)}
                  style={{ width: 14, height: 14, accentColor: '#185FA5' }}
                />
                On
              </label>
            }
          >
            {form.lsaDatesEnabled ? (
              <>
                <FG>
                  <FL>LSA Kick-Off Date</FL>
                  <Input type="date" value={form.lsaKickoffDate || ''} onChange={e => set('lsaKickoffDate', e.target.value)} />
                </FG>
                <FG>
                  <FL>LSA Surveys Completed</FL>
                  <Input type="date" value={form.lsaSurveysCompletedDate || ''} onChange={e => set('lsaSurveysCompletedDate', e.target.value)} />
                </FG>
                <FG>
                  <FL>LSA Tracking Completed <span style={{ fontWeight: 400, textTransform: 'none' }}>(Gladys' phone lines)</span></FL>
                  <Input type="date" value={form.lsaTrackingCompletedDate || ''} onChange={e => set('lsaTrackingCompletedDate', e.target.value)} />
                </FG>
                <FG>
                  <FL>LSA Paid Tracking Completed</FL>
                  <Input type="date" value={form.lsaPaidTrackingCompletedDate || ''} onChange={e => set('lsaPaidTrackingCompletedDate', e.target.value)} />
                </FG>
                <FG>
                  <FL>Completed By</FL>
                  <Input value={form.lsaPaidTrackingCompletedBy || ''} onChange={e => set('lsaPaidTrackingCompletedBy', e.target.value)} placeholder="Name" />
                </FG>
                <FG>
                  <FL>LSA Launch Date</FL>
                  <Input type="date" value={form.lsaLaunchDate || ''} onChange={e => set('lsaLaunchDate', e.target.value)} />
                </FG>
                <DateDiffCallout label="LSA Kick-Off → LSA Launch" fromValue={form.lsaKickoffDate} toValue={form.lsaLaunchDate} />
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#aaa', gridColumn: '1/-1' }}>
                Turned off — flip the toggle above if this client needs separate LSA milestone dates.
              </div>
            )}
          </CollapsibleBlock>

          <FG>
            <FL>Last Checked</FL>
            <Input type="date" value={form.lastChecked || ''} onChange={e => set('lastChecked', e.target.value)} />
          </FG>

          {/* Flags */}
          <FG style={{ justifyContent: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.onHold}
                onChange={e => set('onHold', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#E24B4A' }}
              />
              Mark as On Hold
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.important}
                onChange={e => set('important', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#185FA5' }}
              />
              ★ Mark as Focus Next
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.opspm}
                onChange={e => set('opspm', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#1D9E75' }}
              />
              📋 Ops PM
            </label>
          </FG>

          {/* PM Notes — always visible, only editable by PM/admin */}
          {form.opspm && (
            <FG full style={{ gridColumn: '1/-1' }}>
              <FL style={{ color: '#27500A' }}>📋 PM Notes {!isPM && <span style={{ fontWeight: 400, color: '#aaa' }}>(editable by PM only)</span>}</FL>
              <TA
                value={form.pmNotes || ''}
                onChange={e => set('pmNotes', e.target.value)}
                placeholder="PM notes, comments, status updates..."
                disabled={!isPM}
                style={{
                  minHeight: 80,
                  background: isPM ? '#EAF3DE' : '#f8f8f8',
                  borderColor: isPM ? '#b8dfa0' : 'rgba(0,0,0,0.1)',
                  color: isPM ? '#1a1a18' : '#888',
                  cursor: isPM ? 'text' : 'not-allowed',
                }}
              />
            </FG>
          )}

          {/* Additional account info sections */}
          <FG full>
            <AdsUrlsSection
              websiteUrls={form.adsWebsiteUrls}
              lpUrls={form.adsLpUrls}
              onWebsite={v => set('adsWebsiteUrls', v)}
              onLp={v => set('adsLpUrls', v)}
            />
            <OnboardingAnalysisSection form={form} set={set} />
          </FG>

          {/* LP Build Process — collapsible */}
          {adsDestIsLP && (
            <CollapsibleProcess
              title="🗒 Landing Page Build Process"
              done={lpStepsDone}
              total={LP_STEPS.length}
              accentColor="#BA7517"
              trackColor="#f0e0c0"
              doneColor="#27500A"
              defaultOpen={false}
            >
              <StepChecklist
                steps={LP_STEPS}
                values={form.lpSteps}
                onChange={v => set('lpSteps', v)}
                accentColor="#BA7517"
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginTop: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.lpEnabled}
                  onChange={e => set('lpEnabled', e.target.checked)}
                  style={{ accentColor: '#BA7517' }}
                />
                Show LP progress bar on card
              </label>
            </CollapsibleProcess>
          )}

          {/* Tracking Process — collapsible */}
          <CollapsibleProcess
            title="📡 Tracking Process"
            done={trackStepsDone}
            total={TRACK_STEPS.length}
            accentColor="#1D9E75"
            trackColor="#d0f0e4"
            doneColor="#0F6E56"
            defaultOpen={false}
          >
            <StepChecklist
              steps={TRACK_STEPS}
              values={form.trackSteps}
              onChange={v => set('trackSteps', v)}
              accentColor="#1D9E75"
            />
          </CollapsibleProcess>

          {/* PM To-Do — collapsible, visible to all but editable by PM/admin */}
          <CollapsibleProcess
            title="📋 PM To-Do"
            done={pmStepsDone}
            total={PM_STEPS.length}
            accentColor="#185FA5"
            trackColor="#d0e4f5"
            doneColor="#0C447C"
            defaultOpen={false}
            disabled={!isPM}
          >
            {!isPM && (
              <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginBottom: 8 }}>
                View only — editable by Project Manager
              </div>
            )}
            <StepChecklist
              steps={PM_STEPS}
              values={form.pmSteps}
              onChange={v => isPM && set('pmSteps', v)}
              accentColor="#185FA5"
              disabled={!isPM}
            />
          </CollapsibleProcess>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', bottom: 0, background: '#fff',
          borderRadius: '0 0 12px 12px',
        }}>
          {isViewer ? (
            <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>
              👁 View-only — you do not have edit access
            </div>
          ) : !isNew ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => { if (window.confirm('Delete this client? Cannot be undone.')) onDelete(); }}
                style={{
                  fontSize: 12, padding: '7px 14px', borderRadius: 7,
                  background: '#A32D2D', color: '#fff', border: 'none',
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                }}
              >
                🗑 Delete
              </button>
              {onDuplicate && (
                <button
                  onClick={onDuplicate}
                  style={{
                    fontSize: 12, padding: '7px 14px', borderRadius: 7,
                    background: '#f5f5f3', color: '#555',
                    border: '1px solid rgba(0,0,0,0.18)',
                    cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                  }}
                >
                  ⧉ Duplicate
                </button>
              )}
            </div>
          ) : <div />}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {autosavedAt && (
              <span style={{ fontSize: 11, color: '#0F6E56', fontWeight: 600 }}>
                ✓ Autosaved {new Date(autosavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button onClick={onClose} style={{
              fontSize: 12, padding: '7px 14px', borderRadius: 7,
              background: '#f5f5f3', color: '#333', border: '1px solid rgba(0,0,0,0.18)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {isViewer ? 'Close' : 'Close (autosaved)'}
            </button>
            {!isViewer && (
              <button onClick={handleSave} disabled={saving} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
                cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>
                {saving ? 'Saving…' : '💾 Save Client'}
              </button>
            )}
          </div>
        </div>
      </div>
      {showReport && (
        <ReportModal
          client={form}
          onClose={() => setShowReport(false)}
          onSaved={fields => setForm(f => ({ ...f, ...fields }))}
        />
      )}
      {showImportSurvey && (
        <ImportSurveyModal
          client={form}
          onApply={fields => setForm(f => ({ ...f, ...fields }))}
          onClose={() => setShowImportSurvey(false)}
        />
      )}
      {showSurveyLibrary && (
        <SurveyLibraryModal client={form} onClose={() => setShowSurveyLibrary(false)} />
      )}
    </div>
  );
}
